/* GS Buy Now — theme-owned straight-to-checkout button (sections/main-product.liquid
   buy-buttons block). Rendered only when the block's dynamic checkout button is disabled.
   Adds the form's current variant + quantity to the cart, then redirects to checkout.
   Loaded globally (layout/theme.liquid) because the quick-add drawer injects the PDP form
   markup on collection/search pages, where per-section scripts never load. */

if (!customElements.get('gs-buy-now')) {
  class GsBuyNow extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.gs-buy-now__btn');
      this.form = this.closest('form');
      if (!this.btn || !this.form) return;

      this.btn.addEventListener('click', this.handleClick.bind(this));

      // The Add to Cart button is re-rendered on variant change (data-dynamic-variant-content),
      // this element is not - mirror its disabled state so availability stays in sync.
      this.syncDisabled();
      this.observer = new MutationObserver(this.syncDisabled.bind(this));
      this.observer.observe(this.form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled']
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    syncDisabled() {
      if (this.busy) return;
      const addBtn = this.form.querySelector('button[name="add"]');
      if (!addBtn || addBtn === this.btn) return;
      // Write only on an actual change. `btn.disabled = true` re-sets the attribute even when
      // it is already set, and the observer above (form subtree, attributeFilter ['disabled'])
      // counts that as a fresh mutation - so an unconditional write re-triggers this callback
      // in an endless microtask loop and freezes the page whenever the Add to Cart button is
      // disabled (sold-out product or sold-out selected variant). Keep the equality guard.
      if (this.btn.disabled !== addBtn.disabled) this.btn.disabled = addBtn.disabled;
    }

    /**
     * Adds the form's variant to the cart, then redirects to checkout.
     * @param {object} evt - Click event.
     */
    async handleClick(evt) {
      evt.preventDefault();
      if (this.busy || this.btn.disabled) return;

      const variantId = this.form.querySelector('[name="id"]');
      if (!variantId || !variantId.value) {
        this.showError(this.dataset.textNotSelected);
        return;
      }

      this.busy = true;
      this.btn.classList.add('is-loading');
      this.btn.setAttribute('aria-disabled', 'true');
      this.showError(false);

      try {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const data = await response.json();

        if (!response.ok) {
          this.showError(data.description || data.message || this.dataset.textError);
          return;
        }

        window.location.href = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}checkout`;
      } catch (error) {
        this.showError(this.dataset.textError);
      } finally {
        this.busy = false;
        this.btn.classList.remove('is-loading');
        this.btn.removeAttribute('aria-disabled');
      }
    }

    /**
     * Shows/hides the form's error message box (same element product-form.js uses).
     * @param {string|boolean} [error=false] - Error to show, or false to hide.
     */
    showError(error = false) {
      this.errorMsg = this.errorMsg || this.form.querySelector('.js-form-error');
      if (!this.errorMsg) return;
      this.errorMsg.hidden = !error;
      if (error) this.errorMsg.textContent = error;
    }
  }

  customElements.define('gs-buy-now', GsBuyNow);
}
